import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Stack,
    Typography,
    Button,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    TextField,
} from '@mui/material';
import { ArrowBack, AssignmentReturn, LocalShipping, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useGetMyReturnById } from '../../hooks/customer/useGetMyReturnById';
import { useSubmitReturnShipping } from '../../hooks/customer/useSubmitReturnShipping';
import { useCancelMyReturn } from '../../hooks/customer/useCancelMyReturn';
import {
    RETURN_STATUSES,
    RETURN_REASONS,
    RETURN_CONDITIONS,
} from '../../components/customer/returns/returnConstants';
import ReturnTimeline from '../../components/customer/returns/ReturnTimeline';
import { formatMoney } from '../../utils/money';

const labelOf = (list, value, isArabic) => {
    const found = list.find((x) => x.value === value);
    if (!found) return value;
    return isArabic ? found.labelAr : found.labelEn;
};

const ReturnDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { returnRequest: r, isLoading } = useGetMyReturnById(id);
    const submitShipping = useSubmitReturnShipping();
    const cancelMutation = useCancelMyReturn();

    const [carrier, setCarrier] = useState('');
    const [tracking, setTracking] = useState('');

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!r) {
        return (
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Alert severity="error">{t('returns.notFound', 'Return not found')}</Alert>
            </Container>
        );
    }

    const meta = RETURN_STATUSES[r.status] || { color: 'default', labelEn: r.status, labelAr: r.status };
    const canSubmitShipping = r.status === 'approved';
    const canCancel = ['pending', 'approved'].includes(r.status);
    const refund = r.refund || {};

    const handleSubmitShipping = (e) => {
        e.preventDefault();
        if (!carrier.trim() || !tracking.trim()) return;
        submitShipping.mutate({
            id: r.id,
            data: { carrier: carrier.trim(), tracking_number: tracking.trim() },
        });
    };

    const handleCancel = () => {
        if (!window.confirm(
            isArabic
                ? 'هل أنت متأكد من إلغاء طلب الإرجاع؟'
                : 'Are you sure you want to cancel this return?'
        )) return;
        cancelMutation.mutate({ id: r.id });
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
            <Button
                variant="text"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/account/returns')}
                sx={{ mb: 2 }}
            >
                {t('common.back', 'Back')}
            </Button>

            <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, mb: 2 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 3 }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <AssignmentReturn color="primary" />
                        <Box>
                            <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'monospace' }}>
                                #{r.return_number || r.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('returns.requested', 'Requested')}
                                {': '}
                                {dayjs(r.requested_at || r.created_at).format('YYYY-MM-DD HH:mm')}
                            </Typography>
                        </Box>
                    </Stack>
                    <Chip
                        label={isArabic ? meta.labelAr : meta.labelEn}
                        color={meta.color}
                        sx={{ fontWeight: 'bold', borderRadius: '6px' }}
                    />
                </Stack>

                <Box sx={{ mb: 3 }}>
                    <ReturnTimeline status={r.status} />
                </Box>

                {r.rejection_reason && r.status === 'rejected' && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={700} gutterBottom>
                            {t('returns.rejectionReason', 'Rejection reason')}
                        </Typography>
                        <Typography variant="body2">{r.rejection_reason}</Typography>
                    </Alert>
                )}
            </Paper>

            <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {t('returns.items', 'Items')}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                {(r.items || []).map((it) => (
                    <Stack
                        key={it.id}
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                        sx={{ py: 1.25, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={700}>
                                {it.product?.name?.[isArabic ? 'ar' : 'en']
                                    || it.product?.name?.en
                                    || it.product?.name
                                    || `#${it.id}`}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                <Chip size="small" label={`${t('returns.qty', 'Qty')}: ${it.qty}`} variant="outlined" />
                                {it.reason && (
                                    <Chip
                                        size="small"
                                        label={labelOf(RETURN_REASONS, it.reason, isArabic)}
                                        variant="outlined"
                                    />
                                )}
                                {it.condition && (
                                    <Chip
                                        size="small"
                                        label={labelOf(RETURN_CONDITIONS, it.condition, isArabic)}
                                        variant="outlined"
                                    />
                                )}
                            </Stack>
                            {it.condition_notes && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {it.condition_notes}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                ))}
            </Paper>

            {canSubmitShipping && (
                <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <LocalShipping color="primary" />
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {t('returns.shipBack', 'Ship the items back')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    'returns.shipBackHint',
                                    'Once you ship the items, enter the tracking info so we can mark it received.'
                                )}
                            </Typography>
                        </Box>
                    </Stack>
                    <Box component="form" onSubmit={handleSubmitShipping}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('returns.carrier', 'Carrier')}
                                value={carrier}
                                onChange={(e) => setCarrier(e.target.value)}
                                placeholder="DHL / Aramex / …"
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label={t('returns.trackingNumber', 'Tracking number')}
                                value={tracking}
                                onChange={(e) => setTracking(e.target.value)}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={submitShipping.isPending || !carrier.trim() || !tracking.trim()}
                            >
                                {submitShipping.isPending ? t('common.working', 'Working...') : t('common.save', 'Save')}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            )}

            {(r.refund_amount != null || refund.method) && (
                <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        {t('returns.refund', 'Refund')}
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
                        {r.refund_amount != null && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {t('returns.refundAmount', 'Amount')}
                                </Typography>
                                <Typography variant="h6" fontWeight={700} color="primary.main">
                                    {formatMoney(r.refund_amount, r.currency)}
                                </Typography>
                            </Box>
                        )}
                        {refund.method && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {t('returns.refundMethod', 'Method')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {refund.method}
                                </Typography>
                            </Box>
                        )}
                        {refund.completed_at && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {t('returns.refundCompletedAt', 'Completed at')}
                                </Typography>
                                <Typography variant="body2">
                                    {dayjs(refund.completed_at).format('YYYY-MM-DD HH:mm')}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Paper>
            )}

            {canCancel && (
                <Stack direction="row" justifyContent="flex-end">
                    <Button
                        startIcon={<Cancel />}
                        color="error"
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={cancelMutation.isPending}
                    >
                        {cancelMutation.isPending
                            ? t('common.working', 'Working...')
                            : t('returns.cancel', 'Cancel return')}
                    </Button>
                </Stack>
            )}
        </Container>
    );
};

export default ReturnDetail;
